const hostname = window.location.hostname.toLowerCase();
const isAzureStaticApps = hostname.endsWith("azurestaticapps.net");
const isStaging = isAzureStaticApps && hostname.includes("-staging.");

const isProductionHost =
  (hostname === "imagagrant.com" || hostname === "www.imagagrant.com") ||
  (isAzureStaticApps && !isStaging && hostname.includes("green-desert-06107ed1e"));

const MARKETING_GOOGLE_TAG_ID = process.env.REACT_APP_MARKETING_GOOGLE_TAG_ID || "";
const MARKETING_TAGS_ENABLED = (process.env.REACT_APP_ENABLE_MARKETING_TAGS || "").toLowerCase() === "true";
const SIGNUP_FUNNEL_ID_KEY = "gwc_signup_funnel_id";
const SIGNUP_FUNNEL_STARTED_AT_KEY = "gwc_signup_funnel_started_at";

const EXACT_MARKETING_PATHS = new Set([
  "/",
  "/plans",
  "/compare",
  "/tour",
  "/about",
  "/faq",
  "/resources",
  "/contact",
  "/legal/privacy",
  "/legal/terms",
  "/legal/ai-transparency",
  "/legal/dpa",
]);

function normalizePath(pathname) {
  if (!pathname) return "/";
  const trimmed = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
  return trimmed || "/";
}

function isMarketingPath(pathname) {
  const normalized = normalizePath(pathname);
  if (EXACT_MARKETING_PATHS.has(normalized)) return true;
  if (normalized.startsWith("/compare/")) return true;
  return normalized.startsWith("/resources/articles/");
}

function createFunnelId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `funnel_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getSignupFunnelId() {
  return sessionStorage.getItem(SIGNUP_FUNNEL_ID_KEY);
}

export function ensureSignupFunnelId() {
  const existing = getSignupFunnelId();
  if (existing) {
    return existing;
  }

  const funnelId = createFunnelId();
  sessionStorage.setItem(SIGNUP_FUNNEL_ID_KEY, funnelId);
  sessionStorage.setItem(SIGNUP_FUNNEL_STARTED_AT_KEY, new Date().toISOString());
  return funnelId;
}

export function startSignupFunnel(context = {}) {
  const funnelId = createFunnelId();
  sessionStorage.setItem(SIGNUP_FUNNEL_ID_KEY, funnelId);
  sessionStorage.setItem(SIGNUP_FUNNEL_STARTED_AT_KEY, new Date().toISOString());

  trackMarketingEvent("signup_funnel_started", {
    ...context,
    funnel_id: funnelId,
  });

  return funnelId;
}

export function clearSignupFunnel() {
  sessionStorage.removeItem(SIGNUP_FUNNEL_ID_KEY);
  sessionStorage.removeItem(SIGNUP_FUNNEL_STARTED_AT_KEY);
}

function ensureGoogleTagLoaded(tagId) {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  if (!document.querySelector(`script[data-marketing-tag-id="${tagId}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
    script.dataset.marketingTagId = tagId;
    document.head.appendChild(script);
  }

  if (!window.__marketingGoogleTagConfigured) {
    window.gtag("js", new Date());
    window.gtag("config", tagId, { send_page_view: false });
    window.__marketingGoogleTagConfigured = true;
  }
}

export function trackMarketingPageView(pathname, search = "") {
  if (!MARKETING_TAGS_ENABLED || !MARKETING_GOOGLE_TAG_ID || !isProductionHost) {
    return;
  }

  if (!isMarketingPath(pathname)) {
    return;
  }

  ensureGoogleTagLoaded(MARKETING_GOOGLE_TAG_ID);
  const pagePath = `${normalizePath(pathname)}${search || ""}`;
  window.gtag("config", MARKETING_GOOGLE_TAG_ID, { page_path: pagePath });

  const funnelId = getSignupFunnelId();
  window.gtag("event", "marketing_page_view", {
    page_path: pagePath,
    funnel_id: funnelId || undefined,
  });
}

export function trackMarketingEvent(eventName, params = {}) {
  if (!MARKETING_TAGS_ENABLED || !MARKETING_GOOGLE_TAG_ID || !isProductionHost || !eventName) {
    return;
  }

  ensureGoogleTagLoaded(MARKETING_GOOGLE_TAG_ID);
  const funnelId = getSignupFunnelId();
  const eventParams = {
    ...params,
    funnel_id: params.funnel_id || funnelId || undefined,
    event_timestamp_iso: new Date().toISOString(),
  };
  window.gtag("event", eventName, eventParams);
}
